import { useEffect, useState } from "react";
import { Menu, Phone, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/data/site";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";

const links = [
  { id: "servicios", key: "services" },
  { id: "nosotros", key: "about" },
  { id: "galeria", key: "gallery" },
  { id: "por-que", key: "why" },
  { id: "testimonios", key: "testimonials" },
  { id: "faq", key: "faq" },
  { id: "contacto", key: "contact" },
];

export const Header = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/80 backdrop-blur-xl border-b border-white/10"
          : "bg-gradient-to-b from-black/70 to-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-[72px]">
        <a
          href="#inicio"
          data-testid="header-logo"
          onClick={(e) => {
            e.preventDefault();
            go("inicio");
          }}
          className="flex items-center gap-3 shrink-0"
        >
          <img
            src={CONTACT.logo}
            alt="Angel Cars Upholstery Service"
            className="h-10 w-auto rounded-md"
          />
          <span className="hidden sm:block font-heading font-bold leading-tight text-sm">
            Angel Cars
            <span className="block text-[10px] font-medium tracking-[0.2em] text-zinc-400 uppercase">
              Upholstery Service
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.key}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-zinc-300 hover:text-white transition-colors relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-brand hover:after:w-full after:transition-all"
            >
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="h-9 px-3 rounded-full border border-white/15 text-xs font-semibold text-white hover:border-brand hover:text-brand transition-colors"
          >
            {lang === "es" ? "EN" : "ES"}
          </button>
          <button
            data-testid="header-quote-btn"
            onClick={() => go("cotizador")}
            className="hidden sm:inline-flex items-center gap-2 h-10 px-5 rounded-full bg-brand hover:bg-brand-hover text-white text-sm font-semibold transition-colors"
          >
            <Phone className="h-4 w-4" />
            {t.nav.quote}
          </button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-btn"
                className="lg:hidden h-10 w-10 grid place-items-center rounded-full border border-white/15 text-white"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[#0a0a0a] border-l border-white/10 text-white w-[85%] max-w-sm"
              data-testid="mobile-menu"
            >
              <div className="flex items-center justify-between mb-8">
                <img src={CONTACT.logo} alt="logo" className="h-9 rounded-md" />
                <button onClick={() => setOpen(false)} data-testid="mobile-menu-close">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex flex-col gap-1">
                {links.map((l) => (
                  <button
                    key={l.id}
                    data-testid={`mobile-nav-${l.key}`}
                    onClick={() => go(l.id)}
                    className="text-left py-3 px-2 text-lg font-heading font-medium border-b border-white/5 hover:text-brand transition-colors"
                  >
                    {t.nav[l.key]}
                  </button>
                ))}
                <button
                  data-testid="mobile-quote-btn"
                  onClick={() => go("cotizador")}
                  className="mt-6 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-brand hover:bg-brand-hover text-white font-semibold"
                >
                  <Phone className="h-4 w-4" />
                  {t.nav.quote}
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
